import { createFileRoute, Outlet, useParams } from '@tanstack/react-router'
import { lazy, Suspense, useState } from 'react'
import { type ScenarioView, scenarioLangLabel, useScenarios } from '@/entities/scenario'
import { cn, errorMessage, formatDateTime } from '@/shared/lib'
import { Button } from '@/shared/ui/button'
import { CardLink } from '@/shared/ui/card'
import { Chip } from '@/shared/ui/chip'
import { EmptyState } from '@/shared/ui/empty-state'
import { Icon } from '@/shared/ui/icon'
import { OwnerChip } from '@/shared/ui/owner-chip'
import { OWNER_FILTER_PLATFORM, OwnerFilter, ownerFilterAccount } from '@/shared/ui/owner-filter'
import { PageHeader } from '@/shared/ui/page-header'
import { Spinner } from '@/shared/ui/spinner'

// Форма открывается по кнопке — в стартовый бандл её не тянем: чанк едет на
// первый клик (та же причина, что у lazyRouteComponent на карточках сущностей).
const ScenarioForm = lazy(() =>
  import('@/widgets/scenario-form').then((m) => ({ default: m.ScenarioForm })),
)

export const Route = createFileRoute('/scenarios')({
  component: ScenariosPage,
  staticData: { crumb: 'Сценарии' },
})

// Отбор по владельцу: аккаунт — серверный `?account=`, «платформенные» — по
// пустому владельцу из каталога. Список владельцев для селекта — из каталога
// целиком, иначе отбор прятал бы собственные пункты.
function useScenarioCatalog(owner: string) {
  const catalog = useScenarios()
  const account = ownerFilterAccount(owner)
  const byAccount = useScenarios(account ? { account } : {})
  const owners = [...new Set(catalog.scenarios.map((s) => s.owner_account_id).filter(Boolean))]
  const scenarios =
    owner === OWNER_FILTER_PLATFORM
      ? catalog.scenarios.filter((s) => s.owner_account_id === '')
      : byAccount.scenarios
  return { ...byAccount, scenarios, owners, catalogEmpty: catalog.scenarios.length === 0 }
}

function ScenariosPage() {
  const [owner, setOwner] = useState('')
  const { scenarios, owners, catalogEmpty, isPending, isError, error, refetch } =
    useScenarioCatalog(owner)
  const { scenarioId } = useParams({ strict: false })
  const [addOpen, setAddOpen] = useState(false)

  return (
    <>
      <PageHeader
        title="Сценарии"
        description="Разговор бота: персона, стиль, скрипт и условие завершения"
        actions={
          <Button variant="accent" onClick={() => setAddOpen(true)}>
            <Icon name="plus" size={18} />
            Добавить сценарий
          </Button>
        }
      />

      {isPending ? (
        <div className="flex justify-center py-24 text-muted">
          <Spinner size={28} />
        </div>
      ) : isError ? (
        <EmptyState
          icon="alert"
          title="Не удалось загрузить сценарии"
          description={errorMessage(error)}
          action={
            <Button size="sm" onClick={refetch}>
              Повторить
            </Button>
          }
        />
      ) : catalogEmpty ? (
        <EmptyState
          icon="pen"
          title="Сценариев пока нет"
          description="Заведи сценарий — по нему пойдут демо- и тест-звонки"
          action={
            <Button variant="accent" size="sm" onClick={() => setAddOpen(true)}>
              Добавить сценарий
            </Button>
          }
        />
      ) : (
        <div className="flex flex-col gap-4">
          <div className="flex flex-wrap items-end justify-between gap-3">
            <OwnerFilter className="w-64" owners={owners} value={owner} onChange={setOwner} />
            <p className="text-caption text-muted">
              Платформенный сценарий видит каждое приложение, клиентский — только его аккаунт
            </p>
          </div>

          <div className="flex items-start gap-6">
            <div className="flex w-[400px] shrink-0 flex-col gap-2">
              {scenarios.length === 0 && (
                <EmptyState
                  icon="pen"
                  title="У этого владельца сценариев нет"
                  action={
                    <Button size="sm" onClick={() => setOwner('')}>
                      Все владельцы
                    </Button>
                  }
                />
              )}
              {scenarios.map((scenario) => (
                <ScenarioCard
                  key={scenario.id}
                  scenario={scenario}
                  active={scenarioId === scenario.id}
                />
              ))}
            </div>

            <div className="min-w-0 flex-1">
              <Outlet />
            </div>
          </div>
        </div>
      )}

      {addOpen && (
        <Suspense fallback={null}>
          <ScenarioForm open onClose={() => setAddOpen(false)} />
        </Suspense>
      )}
    </>
  )
}

function ScenarioCard({ scenario, active }: { scenario: ScenarioView; active: boolean }) {
  const collect = (scenario.collect_fields ?? []).length
  return (
    <CardLink to="/scenarios/$scenarioId" params={{ scenarioId: scenario.id }} active={active}>
      <div className="flex items-center justify-between gap-2">
        <span className="truncate text-body-sm font-medium">{scenario.name}</span>
        <Chip tone={active ? 'accent' : 'neutral'}>{scenarioLangLabel(scenario.language)}</Chip>
      </div>
      <p className={cn('mt-0.5 truncate text-body-sm', active ? 'text-white/80' : 'text-muted')}>
        {scenario.id} · выясняет полей: {collect}
      </p>
      <div
        className={cn(
          'mt-3 flex items-center justify-between gap-2 rounded-control px-3 py-2',
          active ? 'bg-black/25' : 'bg-raise',
        )}
      >
        <OwnerChip ownerAccountId={scenario.owner_account_id} />
        <span className={cn('text-caption', active ? 'text-white/70' : 'text-muted')}>
          ревизия {scenario.revision} · {formatDateTime(scenario.updated_at)}
        </span>
      </div>
    </CardLink>
  )
}
